import { Link, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";

export const FeerdMark = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <path d="M6 29V14a10 10 0 0 1 20 0v15" stroke="#C0C0C0" strokeWidth="1.2" />
    <path d="M11 29V15a5 5 0 0 1 10 0v14" stroke="#8A8A8A" strokeWidth="0.8" />
    <circle cx="16" cy="11.5" r="3.2" fill="#F8F4E6" />
    <path d="M3 29h26" stroke="#D8BFD8" strokeWidth="0.8" />
  </svg>
);

const LINKS = [["/conversations", "Conversations"], ["/tasks", "Tasks"], ["/memory", "Memory"], ["/integrations", "Integrations"], ["/settings", "Settings"]];

export const Nav = ({ transparent = false }) => {
  const { user } = useAuth();
  return (
    <header className={`relative z-30 flex items-center justify-between px-6 md:px-12 py-6 ${transparent ? "" : "border-b border-[#1c1c1c]"}`} data-testid="main-nav">
      <Link to="/" className="flex items-center gap-3 group" data-testid="nav-home-link">
        <FeerdMark />
        <span className="font-display text-2xl tracking-[0.18em] text-ivory group-hover:text-white transition-colors duration-300">FEERD</span>
      </Link>
      <nav className="flex items-center gap-5 md:gap-8 text-[0.72rem] tracking-[0.22em] uppercase">
        {user ? <>
          <div className="hidden md:flex items-center gap-8">
            {LINKS.map(([to, label]) => (
              <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase()}-link`}
                className={({ isActive }) => `link-underline whitespace-nowrap transition-colors duration-200 ${isActive ? "text-[#D8BFD8]" : "text-[#8A8A8A] hover:text-ivory"}`}>
                {label}
              </NavLink>
            ))}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger className="md:hidden flex items-center gap-2 text-[#C0C0C0]" data-testid="nav-mobile-menu-button" aria-label="Open menu"><Menu className="h-4 w-4" strokeWidth={1.5} /> Menu</DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#121212] border-[#2A2A2A] rounded-none min-w-[12rem]">
              {LINKS.map(([to, label]) => (
                <DropdownMenuItem key={to} asChild><Link to={to} data-testid={`nav-mobile-${label.toLowerCase()}-link`} className="text-xs tracking-[0.2em] uppercase text-[#C0C0C0] py-3">{label}</Link></DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </> : (
          <Link to="/auth" className="link-underline text-[#C0C0C0] hover:text-ivory" data-testid="nav-signin-link">Sign in</Link>
        )}
      </nav>
    </header>
  );
};

export const Page = ({ eyebrow, title, intro, children, action }) => (
  <div className="min-h-screen bg-[#0A0A0A]">
    <Nav />
    <motion.main initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="max-w-6xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-32">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
        <div className="max-w-2xl">
          <p className="eyebrow mb-5">{eyebrow}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ivory leading-[1.02]" data-testid="page-title">{title}</h1>
          {intro && <p className="mt-6 text-sm md:text-base text-[#8A8A8A] leading-relaxed max-w-xl">{intro}</p>}
        </div>
        {action}
      </div>
      {children}
    </motion.main>
  </div>
);

export const StatusBadge = ({ status, testId }) => {
  const tone = {
    completed: "text-[#C0C0C0] border-[#C0C0C0]/40", working: "text-[#C0C0C0] border-[#C0C0C0]/40", executed: "text-[#C0C0C0] border-[#C0C0C0]/40",
    sandbox_tested: "text-[#C0C0C0] border-[#C0C0C0]/40",
    running: "text-[#D8BFD8] border-[#D8BFD8]/50", queued: "text-[#D8BFD8] border-[#D8BFD8]/30", pending: "text-[#D8BFD8] border-[#D8BFD8]/50", executing: "text-[#D8BFD8] border-[#D8BFD8]/50",
    configured_untested: "text-[#E6E6FA] border-[#E6E6FA]/30",
    failed: "text-[#e2a6a6] border-[#e2a6a6]/40", rejected: "text-[#8A8A8A] border-[#2A2A2A]", cancelled: "text-[#8A8A8A] border-[#2A2A2A]",
    awaiting_credentials: "text-[#e8d5a8] border-[#e8d5a8]/40", not_implemented: "text-[#6a6a6a] border-[#2A2A2A]",
  }[status] || "text-[#8A8A8A] border-[#2A2A2A]";
  return <span data-testid={testId} className={`inline-flex items-center px-2.5 py-1 border text-[0.62rem] tracking-[0.2em] uppercase whitespace-nowrap ${tone}`}>{String(status).replace(/_/g, " ")}</span>;
};
